import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState } from 'react';
import { observer } from 'mobx-react-lite';
import { Dialog, DialogActions, DialogContent, DialogTitle, Stack, } from '@mui/material';
import ChatInput from './ChatInput';
import StreamingMessages from './StreamingMessages';
// import { MediaCardConfig } from '../../../portal-lib/src/portal/item/card/VerticalMediaCard';
import useConversation from './useConversation';
import DismissableMessage from './DismissableMessage';
import { useConversationStore } from '../ConversationStoreProvider';
import { useTokens } from '../tokens/TokensProvider';
import buildSx from './buildSx';
const popupSx = (popupPosition) => ({
    position: 'fixed',
    width: popupPosition.widthPercent ? `${popupPosition.widthPercent}%` : '30%',
    height: popupPosition.heightPercent ? `${popupPosition.heightPercent}%` : '70%',
    minWidth: popupPosition.minWidth ? `${popupPosition.minWidth}px` : '300px',
    minHeight: popupPosition.minHeight ? `${popupPosition.minHeight}px` : '500px',
    ...(popupPosition.position === 'bottom-right' ? {
        bottom: `${popupPosition.paddingY}px`,
        right: `${popupPosition.paddingX}px`,
    } : {}),
    ...(popupPosition.position === 'bottom-center' ? {
        bottom: `${popupPosition.paddingY}px`,
        left: 0,
        right: 0,
        margin: '0 auto',
    } : {}),
    ...(popupPosition.position === 'bottom-left' ? {
        bottom: `${popupPosition.paddingY}px`,
        left: `${popupPosition.paddingX}px`,
    } : {}),
    ...(popupPosition.position === 'top-right' ? {
        top: `${popupPosition.paddingY}px`,
        right: `${popupPosition.paddingX}px`,
    } : {}),
    ...(popupPosition.position === 'top-center' ? {
        top: `${popupPosition.paddingY}px`,
        left: 0,
        right: 0,
        margin: '0 auto',
    } : {}),
    ...(popupPosition.position === 'top-left' ? {
        top: `${popupPosition.paddingY}px`,
        left: `${popupPosition.paddingX}px`,
    } : {}),
});
const ConversationDialog = ({ title = 'Virtual Coach', messageConfig, mediaCardConfig, open, inputStyle, 
// eslint-disable-next-line @typescript-eslint/no-unused-vars
startingMessageFontStyle, handleClose, popupPosition, sendImage, enableVoiceInput, windowStyle, headerStyle, resetConversationLabel, animatedProcessingImage, agentName, }) => {
    const store = useConversationStore();
    const tokens = useTokens();
    const conversation = store.currentConversation;
    const { isAIServiceUnavailable, stylesheet } = store;
    const [isErrorDismissed, setErrorDismissed] = useState(false);
    const getTokenValue = (key) => {
        const tokenValue = tokens.getValue(key);
        if (!tokenValue || tokenValue.trim() === '' || tokenValue === `{{${key}}}`) {
            return '';
        }
        return tokenValue;
    };
    const resolvedPersistentPrompts = store.clientAIPersistentPrompts?.trim()
        ? store.clientAIPersistentPrompts
        : getTokenValue('persistentPrompts');
    const { append, handleInputChange, handleSubmit, input, isLoading, error, reset, saveFeedback, } = useConversation({ agentName }, // options
    {}, []);
    if (!conversation) {
        return null;
    }
    const showErrorMessage = !isLoading && (isAIServiceUnavailable || error) && !isErrorDismissed;
    return (_jsxs(Dialog, { open: open, onClose: handleClose, scroll: "paper", "aria-labelledby": "virtual-coach-title", "aria-describedby": "virtual-coach-description", keepMounted: true, PaperProps: {
            sx: {
                ...buildSx(windowStyle, stylesheet, {}),
                ...popupSx(popupPosition),
            },
        }, children: [_jsx(DialogTitle, { id: "virtual-coach-title", sx: buildSx(headerStyle, stylesheet, {}), children: title }), _jsxs(_Fragment, { children: [_jsx(DialogContent, { dividers: true, children: _jsx(Stack, { direction: "column", spacing: 1, margin: 1, flexGrow: 1, justifyContent: "center", sx: { height: '100%' }, children: _jsx(StreamingMessages, { messageConfig: messageConfig, mediaCardConfig: mediaCardConfig, isLoading: isLoading, saveFeedback: saveFeedback, isMobileView: false, animatedProcessingImage: animatedProcessingImage, topSpaceReserved: "0px" }) }) }), _jsx(DialogActions, { children: _jsxs(Stack, { direction: "column", spacing: 1, margin: 1, flexGrow: 1, justifyContent: "center", children: [showErrorMessage && (_jsx(DismissableMessage, { message: "Service Temporarily Unavailable.", onClose: () => setErrorDismissed(true) })), _jsx(ChatInput, { input: input, handleInputChange: handleInputChange, handleSubmit: handleSubmit, enableVoiceInput: Boolean(enableVoiceInput), isLoading: isLoading, append: append, 
                                    // suggestedPrompts={conversation.hasMessages()
                                    //   ? undefined
                                    //   : store.clientAISuggestedPrompts}
                                    persistentPrompts: resolvedPersistentPrompts, sendImage: sendImage, style: {
                                        width: '100%',
                                        ...inputStyle,
                                    }, reset: reset, resetConversationLabel: resetConversationLabel })] }) })] })] }));
};
ConversationDialog.defaultProps = {
    agentName: '',
    animatedProcessingImage: undefined,
};
export const conversationDialogMeta = [
    {
        key: 'title',
        name: 'Popup Title',
        type: 'text',
        description: 'Title for popup dialog.',
    },
    {
        key: 'sendImage',
        name: 'Send Button Image',
        type: 'imageAsset',
        description: 'Image to show in Send Message text box.',
    },
    {
        key: 'resetConversationLabel',
        name: 'Reset Conversation Label',
        type: 'text',
        description: 'Label for Reset Conversation button.',
    },
    {
        key: 'showItemReferences',
        name: 'Show Item References With Message',
        type: 'boolean',
        description: 'Displays relevent content next to the message.',
    },
    {
        key: 'courseCardShowImage',
        name: 'Show Image',
        type: 'boolean',
        description: 'Show or hide the image in the course card.',
    },
    {
        key: 'courseCardShowTitle',
        name: 'Show Title',
        type: 'boolean',
        description: 'Show or hide the title in the course card.',
    },
    {
        key: 'courseCardShowInstructor',
        name: 'Show Instructor',
        type: 'boolean',
        description: 'Show or hide the instructor in the course card.',
    },
    {
        key: 'courseCardShowPrice',
        name: 'Show Price',
        type: 'boolean',
        description: 'Show or hide the price in the course card.',
    },
    {
        key: 'courseCardShowDescLines',
        name: 'Number of Description Lines',
        type: 'number',
        description: 'Number of description lines to show in the course card.',
    },
    {
        key: 'questionStyle',
        name: 'Question Style',
        type: 'boxStyle',
        description: 'Style of user questions.',
        group: 'style',
    },
    {
        key: 'responseStyle',
        name: 'Response Style',
        type: 'boxStyle',
        description: 'Style of AI response.',
        group: 'style',
    },
    {
        key: 'inputStyle',
        name: 'Input Style',
        type: 'boxStyle',
        description: 'Style of messsage input area.',
        group: 'style',
    },
    {
        key: 'popupPosition',
        name: 'Popup Position',
        type: 'popupPosition',
        description: 'Control positioning of the popup window.',
        // group: 'style',
    },
    {
        key: 'windowStyle',
        name: 'Window Style',
        type: 'boxStyle',
        description: 'Style of window.',
        group: 'style',
    },
    {
        key: 'headerStyle',
        name: 'Header Style',
        type: 'boxStyle',
        description: 'Style of window header.',
        group: 'style',
    },
    {
        key: 'clientAIAnimatedProcessingIcon',
        name: 'Animated Processing Icon',
        type: 'imageAsset',
        description: 'Image to show in Send Message text box.',
    },
    {
        key: 'defaultProcessingIcon.color',
        name: 'Default Processing Icon Color',
        type: 'color',
        description: 'Color of the animated processing icon.',
    },
    {
        key: 'defaultProcessingIcon.width',
        name: 'Default Processing Icon Width',
        type: 'number',
        description: 'Width of the animated processing icon.',
    },
    {
        key: 'defaultProcessingIcon.height',
        name: 'Default Processing Icon Height',
        type: 'number',
        description: 'Height of the animated processing icon.',
    },
    {
        key: 'enableMultiRefinements',
        name: 'Enable Multi-Refinements',
        type: 'boolean',
        description: 'Allow users to select multiple refinements before submitting.',
    },
    {
        key: 'multiRefinementSubmitButtonText',
        name: 'Multi-Refinement Submit Button Text',
        type: 'text',
        description: 'Text to display on the submit button for multi-refinements.',
        default: 'Submit Refinements',
    },
    {
        key: 'agentName',
        name: 'Agent Name',
        type: 'text',
        description: 'Optional agent identifier for AI reporting.',
    },
];
export const conversationDialogDefaults = {
    title: 'Virtual Coach',
    showItemReferences: false,
    courseCardShowImage: true,
    courseCardShowTitle: true,
    courseCardShowInstructor: false,
    courseCardShowDescLines: 2,
    courseCardShowPrice: false,
    clientAIAnimatedProcessingIcon: undefined,
    resetConversationLabel: 'New Conversation',
    questionStyle: {
        backgroundColor: '#dcdcdc',
        borderColor: '#9b9b9b',
        borderRadius: 8,
        borderWidth: 1,
        color: 'color_font',
        margin: 0,
        padding: 12,
        shadow: 1,
    },
    responseStyle: {
        backgroundColor: 'color_background',
        borderColor: '#9b9b9b',
        borderRadius: 8,
        borderWidth: 1,
        color: 'color_font',
        margin: 0,
        padding: 12,
        shadow: 1,
    },
    inputStyle: {
        backgroundColor: 'rgba(0,0,0,0)',
        borderColor: 'color_primary',
        borderRadius: 2,
        borderWidth: 0,
        color: 'color_font',
        margin: 4,
        padding: 4,
        shadow: 0,
    },
    popupPosition: {
        position: 'top-center',
        paddingX: 20,
        paddingY: 20,
        widthPercent: 50,
        minWidth: 300,
        heightPercent: 60,
        minHeight: 400,
    },
    windowStyle: {
        backgroundColor: 'color_background',
        borderColor: 'color_primary',
        borderRadius: 2,
        borderWidth: 0,
        color: 'color_font',
        margin: 4,
        padding: 4,
        shadow: 0,
    },
    headerStyle: {
        backgroundColor: 'color_secondary',
        borderColor: 'color_primary',
        borderRadius: 0,
        borderWidth: 0,
        color: 'color_font',
        margin: 0,
        padding: 0,
        shadow: 0,
    },
    enableVoiceInput: false,
    agentName: '',
};
export default observer(ConversationDialog);
//# sourceMappingURL=ConversationDialog.js.map